class AnalysisApp {
    constructor(options) {
        this.dataProvider = options.dataProvider;
        this.echarts = options.echarts;
        this.renderer = options.renderer;
        this.styles = options.styles || (this.renderer ? this.renderer.getStyles() : []);
        this.chartIds = options.chartIds || [];
        this.currentMode = 'normal'; // Track current mode for resize handling
        this.updateDashboard = this.updateDashboard.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this._transformedData = null; // Cache für transformierte Daten
        this._resizeTimeout = null; // Debounce for resize
    }

    /**
     * Transformiert Scorer-Output (styles.DRIVER.blocks.*) in das
     * von AnalysisApp erwartete Format (normal.DRIVER.a/v, stress.DRIVER.a/v).
     * 
     * Scorer-Format:
     *   { styles: { DRIVER: { blocks: { Absicht_guenstig: 3, ... } } }, scoring_rules: {...} }
     * 
     * Erwartetes Format:
     *   { normal: { DRIVER: { a: 3, v: 5 } }, stress: { DRIVER: { a: 2, v: 7 } }, comp: {...}, global_summary: {...} }
     */
    transformScorerData(raw) {
        if (!raw || !raw.styles || typeof raw.styles !== 'object') return null;

        // Block-Mapping ermitteln (aus scoring_rules oder Defaults)
        const rules = raw.scoring_rules && typeof raw.scoring_rules === 'object' && !Array.isArray(raw.scoring_rules)
            ? raw.scoring_rules
            : {};
        const mapping = rules.block_mapping || {};

        // Standard Block-Namen
        let normalAbsichtBlock  = 'Absicht_guenstig';
        let normalVerhaltenBlock = 'Verhalten_guenstig';
        let stressAbsichtBlock  = 'Absicht_unguenstig';
        let stressVerhaltenBlock = 'Verhalten_unguenstig';

        // Mapping auswerten (wie in resolveValues der public-display)
        const mappingKeys = Object.keys(mapping);
        mappingKeys.forEach(key => {
            const kl = key.toLowerCase();
            const target = mapping[key];
            if (kl.includes('absicht') && (kl.includes('normal') || kl.includes('günstig') || kl.includes('guenstig')) && !kl.includes('un')) {
                normalAbsichtBlock = target;
            } else if (kl.includes('verhalten') && (kl.includes('normal') || kl.includes('günstig') || kl.includes('guenstig')) && !kl.includes('un')) {
                normalVerhaltenBlock = target;
            } else if (kl.includes('absicht') && (kl.includes('stress') || kl.includes('ungünstig') || kl.includes('unguenstig'))) {
                stressAbsichtBlock = target;
            } else if (kl.includes('verhalten') && (kl.includes('stress') || kl.includes('ungünstig') || kl.includes('unguenstig'))) {
                stressVerhaltenBlock = target;
            }
        });

        // Fallback: Block-Namen direkt aus den Blocks erkennen (ohne Mapping)
        if (mappingKeys.length === 0) {
            const firstStyle = Object.values(raw.styles)[0];
            if (firstStyle && firstStyle.blocks) {
                Object.keys(firstStyle.blocks).forEach(bn => {
                    const bl = bn.toLowerCase();
                    // Pattern 1: guenstig / unguenstig
                    if (bl.includes('absicht') && (bl.includes('günstig') || bl.includes('guenstig')) && !bl.includes('un')) normalAbsichtBlock = bn;
                    else if (bl.includes('verhalten') && (bl.includes('günstig') || bl.includes('guenstig')) && !bl.includes('un')) normalVerhaltenBlock = bn;
                    else if (bl.includes('absicht') && (bl.includes('ungünstig') || bl.includes('unguenstig'))) stressAbsichtBlock = bn;
                    else if (bl.includes('verhalten') && (bl.includes('ungünstig') || bl.includes('unguenstig'))) stressVerhaltenBlock = bn;
                    // Pattern 2: _normal / _stress
                    else if (bl.includes('absicht') && bl.includes('normal') && !bl.includes('stress')) normalAbsichtBlock = bn;
                    else if (bl.includes('verhalten') && bl.includes('normal') && !bl.includes('stress')) normalVerhaltenBlock = bn;
                    else if (bl.includes('absicht') && bl.includes('stress')) stressAbsichtBlock = bn;
                    else if (bl.includes('verhalten') && bl.includes('stress')) stressVerhaltenBlock = bn;
                });
            }
        }

        console.log('[ShapeFactor] Block-Mapping:', { normalAbsichtBlock, normalVerhaltenBlock, stressAbsichtBlock, stressVerhaltenBlock });

        // Daten transformieren
        const normal = {};
        const stress = {};

        Object.keys(raw.styles).forEach(styleKey => {
            const styleData = raw.styles[styleKey];
            const blocks = styleData.blocks || {};

            // Block-Wert lesen mit Fuzzy-Fallback
            const getBlock = (blockName) => {
                if (blocks[blockName] !== undefined) return Number(blocks[blockName] || 0);
                // Fuzzy: case-insensitive Suche
                const lower = blockName.toLowerCase();
                const found = Object.keys(blocks).find(k => k.toLowerCase() === lower);
                return found !== undefined ? Number(blocks[found] || 0) : 0;
            };

            normal[styleKey] = {
                a: getBlock(normalAbsichtBlock),
                v: getBlock(normalVerhaltenBlock),
                absicht: getBlock(normalAbsichtBlock),
                verhalten: getBlock(normalVerhaltenBlock),
                text: styleData.report?.dimensions?.Absicht_normal || ''
            };

            stress[styleKey] = {
                a: getBlock(stressAbsichtBlock),
                v: getBlock(stressVerhaltenBlock),
                absicht: getBlock(stressAbsichtBlock),
                verhalten: getBlock(stressVerhaltenBlock),
                text: styleData.report?.dimensions?.Absicht_stress || ''
            };
        });

        return {
            normal,
            stress,
            comp: raw.styles,
            global_summary: raw.global_summary || {},
            scoring_rules: raw.scoring_rules || {},
            dominant_style: raw.dominant_style || null,
            dominant_style_stress: raw.dominant_style_stress || null
        };
    }

    getSourceData() {
        // Cache verwenden wenn vorhanden
        if (this._transformedData) return this._transformedData;

        if (window.sfUserResult) {
            // Prüfen: Ist es das alte Format (mit normal/stress) oder das neue Scorer-Format (mit styles)?
            if (window.sfUserResult.normal && typeof window.sfUserResult.normal === 'object') {
                // Altes Format: direkt verwenden
                console.log('[ShapeFactor] Datenformat: Legacy (normal/stress)');
                this._transformedData = window.sfUserResult;
            } else if (window.sfUserResult.styles && typeof window.sfUserResult.styles === 'object') {
                // Neues Scorer-Format: transformieren
                console.log('[ShapeFactor] Datenformat: Scorer (styles/blocks) → wird transformiert');
                this._transformedData = this.transformScorerData(window.sfUserResult);
            }

            if (this._transformedData) {
                console.log('[ShapeFactor] Transformierte Daten:', this._transformedData);
                return this._transformedData;
            }
        }

        return this.dataProvider.getData();
    }

    getCompData() {
        const source = this.getSourceData();
        return source.comp || {};
    }

    getSummaryJson(values) {
        // Fix: Ensure we return only the global_summary part if it exists, matching DummyData structure
        if (window.sfUserResult && window.sfUserResult.global_summary) {
            const gs = window.sfUserResult.global_summary;
            // Prüfen ob es tatsächlich Inhalt hat (nicht nur leeres Array/Objekt)
            if (gs.dominant_style || gs.stress_profile) {
                return gs;
            }
        }
        // Auch aus transformierten Daten lesen
        const source = this.getSourceData();
        if (source.global_summary && (source.global_summary.dominant_style || source.global_summary.stress_profile)) {
            return source.global_summary;
        }
        return this.dataProvider.getMockJson(values, this.styles);
    }

    updateDashboard(mode) {
        if (!this.renderer) return;
        this.currentMode = mode; // Update current mode

        const dom = {
            pie: document.getElementById('pieAngle'),
            beforeAfter: document.getElementById('beforeAfter'),
            overviewTable: document.getElementById('overviewTable') || document.getElementById('overviewTableSection')
        };

        document.querySelectorAll('.mode-btn').forEach(btn => {
            const active = btn.dataset.mode === mode;
            btn.classList.toggle('bg-white', active);
            btn.classList.toggle('shadow-sm', active);
            btn.classList.toggle('border', active);
            btn.classList.toggle('border-gray-200', active);
            btn.classList.toggle('text-brand', active);
            btn.classList.toggle('text-gray-600', !active);
        });

        const values = {
            normal: { absicht: {}, verhalten: {} },
            stress: { absicht: {}, verhalten: {} },
            comp: this.getCompData()
        };

        const sourceData = this.getSourceData();

        this.styles.forEach(s => {
            const findStyleData = (modeObj, styleKey) => {
                if (!modeObj) return null;
                return modeObj[styleKey] || modeObj[styleKey.toLowerCase()] || modeObj[styleKey.toUpperCase()];
            };

            const normalData = findStyleData(sourceData.normal, s.key);
            const stressData = findStyleData(sourceData.stress, s.key);

            if (normalData) {
                values.normal.absicht[s.key] = Number(normalData.a || normalData.absicht || 0);
                values.normal.verhalten[s.key] = Number(normalData.v || normalData.verhalten || 0);
            } else {
                const fallback = this.dataProvider.getData().normal[s.key];
                values.normal.absicht[s.key] = fallback?.a || 0;
                values.normal.verhalten[s.key] = fallback?.v || 0;
            }

            if (stressData) {
                values.stress.absicht[s.key] = Number(stressData.a || stressData.absicht || 0);
                values.stress.verhalten[s.key] = Number(stressData.v || stressData.verhalten || 0);
            } else {
                const fallback = this.dataProvider.getData().stress[s.key];
                values.stress.absicht[s.key] = fallback?.a || 0;
                values.stress.verhalten[s.key] = fallback?.v || 0;
            }
        });

        const summaryJson = this.getSummaryJson(values);

        if (dom.pie) {
            const chartPie = this.echarts.getInstanceByDom(dom.pie) || this.echarts.init(dom.pie, null, { renderer: 'canvas' });
            this.renderer.renderPiePadAngleUnified(chartPie, this.styles, values, mode);
        }

        if (dom.beforeAfter) {
            const chartBefore = this.echarts.getInstanceByDom(dom.beforeAfter) || this.echarts.init(dom.beforeAfter, null, { renderer: 'canvas' });
            this.renderer.renderBeforeAfter(chartBefore, this.styles, values[mode], null, mode);
        }

        this.styles.forEach(s => {
            const domG = document.getElementById('g_' + s.key.toLowerCase());
            if (domG) {
                const chartG = this.echarts.getInstanceByDom(domG) || this.echarts.init(domG, null, { renderer: 'canvas' });
                const valA = values[mode].absicht[s.key];
                const valV = values[mode].verhalten[s.key];
                const diff = valV - valA;
                this.renderer.renderGauge(chartG, s.name, s.color, diff);
            }
        });

        this.renderer.renderOverviewTable(dom.overviewTable, this.styles, values);
        this.renderer.buildGlobalSummary(summaryJson, this.styles, values, mode);
        this.renderer.buildEvaluationRow(null, this.styles, values, mode, this.dataProvider);
    }

    handleResize() {
        // Debounce resize to prevent performance issues
        if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
        
        // Immediate resize for basic responsiveness
        this.chartIds.forEach(id => {
            const el = document.getElementById(id);
            if (el) {
                const inst = this.echarts.getInstanceByDom(el);
                if (inst) inst.resize();
            }
        });

        // Delayed full update to recalculate custom graphics (Pie Chart lines/logo)
        this._resizeTimeout = setTimeout(() => {
            if (this.currentMode) {
                this.updateDashboard(this.currentMode);
            }
        }, 200);
    }

    handleScroll() {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrolled = (window.scrollY / docHeight) * 100;
        const bar = document.getElementById('progressBar');
        if (bar) bar.style.width = scrolled + '%';
        document.querySelectorAll('.reveal').forEach(el => {
            const top = el.getBoundingClientRect().top;
            if (top < window.innerHeight * 0.85) el.classList.add('active');
        });
    }

    init() {
        // Cache zurücksetzen bei jedem Init (falls Daten sich geändert haben)
        this._transformedData = null;

        if (document.getElementById('pieAngle') || document.getElementById('resultsApp')) {
            document.querySelectorAll('.mode-btn').forEach(btn => {
                btn.addEventListener('click', () => this.updateDashboard(btn.dataset.mode));
            });
            this.updateDashboard('normal');
        }
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleScroll);
    }
}

export default AnalysisApp;